import type { DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  GET_ADJUSTMENT_CATEGORIES_LOOKUP,
  GET_DEDUCTION_PRIORITIES_LOOKUP,
  GET_DEDUCTION_STAGES_LOOKUP,
  GET_DEDUCTION_VALIDITIES_LOOKUP,
  GET_DEDUCTION_VALIDITY_RCAS_LOOKUP,
} from "@src/domain/graphql/masterData";
import type {
  AdjustmentCategoriesLookupResponse,
  AdjustmentCategoryLookupItem,
  DeductionPrioritiesLookupResponse,
  DeductionPriorityLookupItem,
  DeductionStagesLookupResponse,
  DeductionStageLookupItem,
  DeductionValiditiesLookupResponse,
  DeductionValidityLookupItem,
  DeductionValidityRcaLookupItem,
  DeductionValidityRcasLookupResponse,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataTenantLookupsRepository,
  LookupListResult,
} from "@src/domain/repositories/masterData";
import { throwIfMasterDataGraphQLError } from "./masterDataRepository.shared";

async function queryLookup<TData, TItem>(
  brandKey: string,
  query: DocumentNode,
  read: (data: TData | null | undefined) => { items: TItem[] | null; count: number } | undefined,
  fallbackMessage: string,
  variables?: Record<string, unknown>,
): Promise<LookupListResult<TItem>> {
  const { data, error } = await MasterDataGraphQlAdapter.getInstance()
    .getClient()
    .query<TData>({
      query,
      variables,
      fetchPolicy: "network-only",
      errorPolicy: "all",
      context: { brandKey },
    });

  throwIfMasterDataGraphQLError({ error });

  const response = read(data);
  if (!response) {
    throw new Error(fallbackMessage);
  }

  return {
    items: response.items ?? [],
    count: response.count ?? 0,
  };
}

export class MasterDataTenantLookupsRepository implements IMasterDataTenantLookupsRepository {
  getAdjustmentCategoriesLookup(
    brandKey: string,
  ): Promise<LookupListResult<AdjustmentCategoryLookupItem>> {
    return queryLookup<AdjustmentCategoriesLookupResponse, AdjustmentCategoryLookupItem>(
      brandKey,
      GET_ADJUSTMENT_CATEGORIES_LOOKUP,
      (data) => data?.masterDataAdjustmentCategoriesLookup,
      "Failed to fetch adjustment categories lookup",
    );
  }

  getDeductionPrioritiesLookup(
    brandKey: string,
  ): Promise<LookupListResult<DeductionPriorityLookupItem>> {
    return queryLookup<DeductionPrioritiesLookupResponse, DeductionPriorityLookupItem>(
      brandKey,
      GET_DEDUCTION_PRIORITIES_LOOKUP,
      (data) => data?.masterDataDeductionPrioritiesLookup,
      "Failed to fetch deduction priorities lookup",
    );
  }

  getDeductionStagesLookup(brandKey: string): Promise<LookupListResult<DeductionStageLookupItem>> {
    return queryLookup<DeductionStagesLookupResponse, DeductionStageLookupItem>(
      brandKey,
      GET_DEDUCTION_STAGES_LOOKUP,
      (data) => data?.masterDataDeductionStagesLookup,
      "Failed to fetch deduction stages lookup",
    );
  }

  getDeductionValiditiesLookup(
    brandKey: string,
  ): Promise<LookupListResult<DeductionValidityLookupItem>> {
    return queryLookup<DeductionValiditiesLookupResponse, DeductionValidityLookupItem>(
      brandKey,
      GET_DEDUCTION_VALIDITIES_LOOKUP,
      (data) => data?.masterDataDeductionValiditiesLookup,
      "Failed to fetch deduction validities lookup",
    );
  }

  getDeductionValidityRcasLookup(
    brandKey: string,
    deductionTypeId?: number | null,
  ): Promise<LookupListResult<DeductionValidityRcaLookupItem>> {
    return queryLookup<DeductionValidityRcasLookupResponse, DeductionValidityRcaLookupItem>(
      brandKey,
      GET_DEDUCTION_VALIDITY_RCAS_LOOKUP,
      (data) => data?.masterDataDeductionValidityRcasLookup,
      "Failed to fetch deduction validity RCA lookup",
      deductionTypeId == null ? undefined : { deductionTypeId },
    );
  }
}
